<template>
  <div class="min-h-screen bg-white">
    <Head :title="specialist.full_name" />

    <!-- Back Link -->
    <div class="bg-white shadow">
      <div class="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <!-- TODO:
        <Link
          :href="$routes.specialists_index({ site_context: specialist.site_context })"
          class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Specialists
        </Link> -->
      </div>
    </div>

    <!-- Specialist Detail -->
    <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div class="overflow-hidden bg-white shadow-lg sm:rounded-lg">
        <div class="md:flex">
          <!-- Photo -->
          <div class="md:w-1/3">
            <div class="aspect-[3/4] overflow-hidden bg-gray-200">
              <img
                v-if="specialist.photo_url"
                :src="specialist.photo_url"
                :alt="specialist.full_name"
                class="h-full w-full object-cover"
              >
              <div
                v-else
                class="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400"
              >
                <svg class="h-32 w-32 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 p-6 md:p-8">
            <div class="mb-2 text-sm font-medium text-indigo-600">
              {{ site_context_label }}
            </div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ specialist.full_name }}
            </h1>
            <p v-if="specialist.title" class="mt-2 text-xl text-gray-600">
              {{ specialist.title }}
            </p>

            <!-- Description -->
            <div v-if="specialist.description" class="mt-6">
              <h2 class="text-lg font-semibold text-gray-900">About</h2>
              <p class="mt-2 whitespace-pre-line text-gray-600">
                {{ specialist.description }}
              </p>
            </div>

            <!-- Specialisations -->
            <div
              v-if="specialist.specialisations && specialist.specialisations.length > 0"
              class="mt-6"
            >
              <h2 class="text-lg font-semibold text-gray-900">Specialisations</h2>
              <div class="mt-3 flex flex-wrap gap-2">
                <Link
                  v-for="specialisation in specialist.specialisations"
                  :key="specialisation.id"
                  :href="$routes.specialisation(specialisation.slug)"
                  class="hover:opacity-80 transition-opacity"
                >
                  <SpecialisationBadge
                    :name="specialisation.name"
                    :site-context="specialisation.site_context"
                  />
                </Link>
              </div>
            </div>

            <!-- Important Information -->
            <div
              v-if="specialist.important_information && specialist.important_information.length > 0"
              class="mt-6"
            >
              <h2 class="text-lg font-semibold text-gray-900">Important Information</h2>
              <ul class="mt-2 list-inside list-disc space-y-1 text-gray-600">
                <li
                  v-for="(info, index) in specialist.important_information"
                  :key="index"
                >
                  {{ info }}
                </li>
              </ul>
            </div>

            <!-- Media -->
            <div
              v-if="specialist.media && specialist.media.length > 0"
              class="mt-6"
            >
              <h2 class="text-lg font-semibold text-gray-900">Media</h2>
              <ul class="mt-2 space-y-2">
                <li
                  v-for="(link, index) in specialist.media"
                  :key="index"
                >
                  <a
                    :href="link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                  >
                    <svg class="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    {{ formatMediaLink(link) }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import SpecialisationBadge from '@/Shared/SpecialisationBadge.vue';

export default {
  components: {
    Head,
    Link,
    SpecialisationBadge,
  },
  layout: Layout,
  props: {
    specialist: {
      type: Object,
      required: true,
    },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: {
      type: String,
      default: '',
    },
  },
  methods: {
    formatMediaLink(link) {
      try {
        const url = new URL(link);
        return url.hostname + (url.pathname !== '/' ? url.pathname : '');
      } catch {
        return link;
      }
    },
  },
};
</script>
